import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Input,
  InputGroup,
  Table,
  Button,
  DOMHelper,
  Stack,
  DateRangePicker,
  useToaster,
  Pagination
} from 'rsuite';
import { useDispatch, useSelector } from 'react-redux';
import SearchIcon from '@rsuite/icons/Search';
import ExportIcon from '@rsuite/icons/Export';
import { MdFilterList } from 'react-icons/md';
import { Icon } from '@rsuite/icons';
import DrawerView from './DrawerView';
import { fetchCombinedModes } from '../../../services/combinedModes.service';
import { getAdminUsers } from '@/services/user.service';
import { format } from 'date-fns';
import * as XLSX from 'xlsx';

const { Column, HeaderCell, Cell } = Table;
const { getHeight } = DOMHelper;

const DataTable = () => {
  const dispatch = useDispatch();
  const toaster = useToaster();
  const { data, loading } = useSelector(state => state.combinedModes);
  const { data: users, loading: usersLoading } = useSelector(state => state.users);
  const [showDrawer, setShowDrawer] = useState(false);
  const [searchKeyword, setSearchKeyword] = useState('');
  const [limit, setLimit] = useState(20);
  const [page, setPage] = useState(1);
  const [filteredData, setFilteredData] = useState([]);
  const [dateRange, setDateRange] = useState(() => {
    const today = new Date();
    const first = today.getDate() - today.getDay();
    const last = first + 6;

    const start = new Date(today.setDate(first));
    start.setHours(0, 0, 0, 0);

    const end = new Date(today.setDate(last));
    end.setHours(23, 59, 59, 999);

    return [start, end];
  });
  const [filters, setFilters] = useState({
    userId: '',
    department: '',
    modes: []
  });
  useEffect(() => {
    dispatch(getAdminUsers());
  }, [dispatch]);

  const applyFilters = (dataToFilter, searchTerm, currentFilters) => {
    let filtered = [...dataToFilter];

    if (searchTerm.trim()) {
      const searchStr = searchTerm.toLowerCase().trim();
      filtered = filtered.filter(item => {
        const fullName = `${item.firstname || ''} ${item.lastname || ''}`;
        return (
          fullName.toLowerCase().includes(searchStr) ||
          (item.registrationNumber?.toLowerCase() || '').includes(searchStr) ||
          (item.department?.toLowerCase() || '').includes(searchStr)
        );
      });
    }

    if (currentFilters.userId) {
      const selectedUser = users.find(user => user._id === currentFilters.userId);
      if (selectedUser) {
        filtered = filtered.filter(item => {
          const itemFullName = `${item.firstname || ''} ${item.lastname || ''}`.toLowerCase();
          const userFullName = `${selectedUser.firstname || ''} ${
            selectedUser.lastname || ''
          }`.toLowerCase();
          return itemFullName === userFullName;
        });
      }
    }

    if (currentFilters.department) {
      filtered = filtered.filter(item => item.department === currentFilters.department);
    }

    if (Array.isArray(currentFilters.modes) && currentFilters.modes.length > 0) {
      filtered = filtered.filter(item => currentFilters.modes.some(mode => (item[mode] || 0) > 0));
    }

    return filtered;
  };

  useEffect(() => {
    if (dateRange && dateRange[0]) {
      const startDate = new Date(dateRange[0]);
      startDate.setHours(0, 0, 0, 0);

      const endDate = dateRange[1] ? new Date(dateRange[1]) : new Date(dateRange[0]);
      endDate.setHours(23, 59, 59, 999);

      dispatch(fetchCombinedModes(format(startDate, 'yyyy-MM-dd')));
    }
  }, [dateRange, dispatch]);

  useEffect(() => {
    const newFilteredData = applyFilters(data, searchKeyword, filters);
    setFilteredData(newFilteredData);
  }, [data, searchKeyword, filters, users]); // Added users dependency for userId filter

  const handleSearch = searchTerm => {
    setSearchKeyword(searchTerm);
    setPage(1);
  };

  const handleFiltersChange = newFilters => {
    console.log('Applying new filters:', newFilters); // Debug log
    setFilters(newFilters);
    setPage(1);
  };

  const handleResetFilters = () => {
    setFilters({
      userId: '',
      department: '',
      modes: []
    });
    setPage(1);
  };

  const downloadExcel = (data, name) => {
    const worksheet = XLSX.utils.json_to_sheet(data);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Sheet1');
    XLSX.writeFile(workbook, `${name}.xlsx`);
  };

  const handleExport = () => {
    const combinedModesToExport = filteredData.map(item => {
      return {
        'Full Name': `${item.firstname || ''} ${item.lastname || ''}`,
        'Registration Number': item.registrationNumber || '',
        Grade: item.grade || '',
        Department: item.department || '',
        WFH: item.WFH || 0,
        'On Site With CheckIn': item.OnSiteWithCheckin || 0,
        'On Site Without CheckIn': item.OnSiteWithoutCheckin || 0,
        'Business Travel': item.TRAVEL || 0,
        'Third Party Site': item.ThirdPartySite || 0,
        Leave: item.Leave || 0,
        Untraced: item.Untraced || 0
      };
    });

    const startDate = format(dateRange[0], 'yyyy-MM-dd');
    const endDate = format(dateRange[1], 'yyyy-MM-dd');
    downloadExcel(combinedModesToExport, `combined-modes-${startDate}-to-${endDate}`);
  };
  const handleChangeLimit = dataKey => {
    setPage(1);
    setLimit(dataKey);
  };
  const { t } = useTranslation();

  return (
    <>
      <Stack className="table-toolbar" justifyContent="space-between">
        <Stack spacing={6}>
          <DateRangePicker
            value={dateRange}
            onChange={setDateRange}
            oneTap
            showOneCalendar
            hoverRange={'week'}
            ranges={[]}
            placeholder={t('Select Week')}
            editable={false}
          />
        </Stack>

        <Stack spacing={6}>
          <InputGroup inside>
            <Input placeholder="Search" value={searchKeyword} onChange={handleSearch} />
            <InputGroup.Addon>
              <SearchIcon />
            </InputGroup.Addon>
          </InputGroup>
          <Button appearance="primary" onClick={() => setShowDrawer(true)}>
            <Icon as={MdFilterList} style={{ marginRight: 8 }} /> {t('Filters')}
          </Button>
          <Button appearance="primary" onClick={handleExport}>
            <ExportIcon style={{ marginRight: 8 }} /> {t('Export')}
          </Button>
        </Stack>
      </Stack>
      <>
        <Table
          height={Math.max(getHeight(window) - 200, 400)}
          data={filteredData}
          loading={loading}
        >
          <Column flexGrow={4}>
            <HeaderCell>{t('First Name & Last Name')}</HeaderCell>
            <Cell>{rowData => `${rowData.firstname || ''} ${rowData.lastname || ''}`}</Cell>
          </Column>

          <Column flexGrow={3}>
            <HeaderCell>{t('Registration Number')}</HeaderCell>
            <Cell dataKey="registrationNumber" />
          </Column>

          <Column flexGrow={2} resizable>
            <HeaderCell>{t('Grade')}</HeaderCell>
            <Cell dataKey="grade" />
          </Column>

          <Column flexGrow={3} resizable>
            <HeaderCell>{t('Team')}</HeaderCell>
            <Cell dataKey="department" />
          </Column>

          <Column flexGrow={2} resizable>
            <HeaderCell>{t('Home office')}</HeaderCell>
            <Cell dataKey="WFH" />
          </Column>

          <Column flexGrow={2} resizable>
            <HeaderCell>{t('On Site With CheckIn')}</HeaderCell>
            <Cell dataKey="OnSiteWithCheckin" />
          </Column>

          <Column flexGrow={2} resizable>
            <HeaderCell>{t('On Site Without CheckIn')}</HeaderCell>
            <Cell dataKey="OnSiteWithoutCheckin" />
          </Column>

          <Column flexGrow={2} resizable>
            <HeaderCell>{t('Third Party Site')}</HeaderCell>
            <Cell dataKey="ThirdPartySite" />
          </Column>

          <Column flexGrow={2} resizable>
            <HeaderCell>{t('Leave')}</HeaderCell>
            <Cell dataKey="Leave" />
          </Column>
          <Column flexGrow={2} resizable>
            <HeaderCell>{t('Business Travel')}</HeaderCell>
            <Cell dataKey="TRAVEL" />
          </Column>

          <Column flexGrow={2} resizable>
            <HeaderCell>{t('Untraced')}</HeaderCell>
            <Cell dataKey="Untraced" />
          </Column>
        </Table>
        <div style={{ padding: 20 }}>
          <Pagination
            prev
            next
            first
            last
            ellipsis
            boundaryLinks
            maxButtons={5}
            size="xs"
            layout={['total', '-', 'limit', '|', 'pager', 'skip']}
            total={filteredData.length}
            limitOptions={[10, 30, 50]}
            limit={limit}
            activePage={page}
            onChangePage={setPage}
            onChangeLimit={handleChangeLimit}
          />
        </div>
      </>

      <DrawerView
        open={showDrawer}
        onClose={() => setShowDrawer(false)}
        onFiltersChange={handleFiltersChange}
        onResetFilters={handleResetFilters}
        currentFilters={filters}
        users={users}
        usersLoading={usersLoading}
        toaster={toaster}
      />
    </>
  );
};

export default DataTable;
